// Centralized color constants for Sleeptronic Maintenance System
// All components should import from this file instead of defining inline
// Supports both light and dark modes

export const navyBlue = '#1e3a5f';

// Mode-aware priority colors
export const getPriorityColors = (mode = 'light') => ({
  low: {
    bg: mode === 'dark' ? '#1b3d1b' : '#e8f5e9',
    border: '#4caf50',
    text: mode === 'dark' ? '#81c784' : '#2e7d32',
  },
  medium: {
    bg: mode === 'dark' ? '#1a3a5c' : '#e3f2fd',
    border: '#2196f3',
    text: mode === 'dark' ? '#64b5f6' : '#1565c0',
  },
  high: {
    bg: mode === 'dark' ? '#4a3000' : '#fff3e0',
    border: '#ff9800',
    text: mode === 'dark' ? '#ffb74d' : '#e65100',
  },
  emergency: {
    bg: mode === 'dark' ? '#4a1515' : '#ffebee',
    border: '#f44336',
    text: mode === 'dark' ? '#ef9a9a' : '#c62828',
  },
});

// Mode-aware status colors
export const getStatusColors = (mode = 'light') => ({
  assigned: {
    bg: mode === 'dark' ? '#4a3000' : '#fff3e0',
    border: '#ff9800',
    text: mode === 'dark' ? '#ffb74d' : '#e65100',
  },
  in_progress: {
    bg: mode === 'dark' ? '#1a3a5c' : '#e3f2fd',
    border: '#2196f3',
    text: mode === 'dark' ? '#64b5f6' : '#1565c0',
  },
  completed: {
    bg: mode === 'dark' ? '#1b3d1b' : '#e8f5e9',
    border: '#4caf50',
    text: mode === 'dark' ? '#81c784' : '#2e7d32',
  },
});

// Mode-aware role colors
export const getRoleColors = (mode = 'light') => ({
  administrator: {
    bg: mode === 'dark' ? '#4a1515' : '#ffebee',
    border: '#f44336',
    text: mode === 'dark' ? '#ef9a9a' : '#c62828',
  },
  supervisor: {
    bg: mode === 'dark' ? '#4a3000' : '#fff3e0',
    border: '#ff9800',
    text: mode === 'dark' ? '#ffb74d' : '#e65100',
  },
  technician: {
    bg: mode === 'dark' ? '#1a3a5c' : '#e3f2fd',
    border: '#2196f3',
    text: mode === 'dark' ? '#64b5f6' : '#1565c0',
  },
  janitor: {
    bg: mode === 'dark' ? '#1b3d1b' : '#e8f5e9',
    border: '#4caf50',
    text: mode === 'dark' ? '#81c784' : '#2e7d32',
  },
  it_technician: {
    bg: mode === 'dark' ? '#2d1b4a' : '#f3e5f5',
    border: '#9c27b0',
    text: mode === 'dark' ? '#ce93d8' : '#7b1fa2',
  },
  office_manager: {
    bg: mode === 'dark' ? '#2d3a2d' : '#e0f2f1',
    border: '#00897b',
    text: mode === 'dark' ? '#80cbc4' : '#00695c',
  },
  operator: {
    bg: mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
    border: '#9e9e9e',
    text: mode === 'dark' ? '#bdbdbd' : '#616161',
  },
});

// Mode-aware overdue colors
export const getOverdueColors = (mode = 'light') => ({
  bg: mode === 'dark' ? '#4a1515' : '#ffebee',
  border: '#d32f2f',
  text: mode === 'dark' ? '#ef9a9a' : '#b71c1c',
});

// Mode-aware background colors for common UI elements
export const getBackgroundColors = (mode = 'light') => ({
  // Page backgrounds
  pageBackground: mode === 'dark' ? '#121212' : '#f5f5f5',
  paperBackground: mode === 'dark' ? '#1e1e1e' : '#ffffff',

  // Table colors
  tableRowAlt: mode === 'dark' ? '#252525' : '#fafafa',
  tableRowHover: mode === 'dark' ? '#2c4a6e' : '#e3f2fd',

  // Border colors
  borderColor: mode === 'dark' ? '#333333' : '#e0e0e0',

  // Card colors
  cardBackground: mode === 'dark' ? '#1e1e1e' : '#ffffff',
  cardHover: mode === 'dark' ? '#252525' : '#ffffff',

  // Drawer/Sidebar
  drawerBackground: mode === 'dark' ? '#1e1e1e' : '#ffffff',

  // Dialog
  dialogBackground: mode === 'dark' ? '#1e1e1e' : '#ffffff',

  // Type chip backgrounds
  pmTypeBackground: mode === 'dark' ? '#1a3a5c' : '#e3f2fd',
  pmTypeText: mode === 'dark' ? '#64b5f6' : '#1565c0',
  defaultTypeBackground: mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
  defaultTypeText: mode === 'dark' ? '#bdbdbd' : '#666666',

  // Miscellaneous
  inputBackground: mode === 'dark' ? '#252525' : '#ffffff',
  hoverBackground: mode === 'dark' ? 'rgba(44, 74, 110, 0.3)' : 'rgba(30, 58, 95, 0.08)',
});

// Mode-aware issue type (assignment_type) colors
export const getIssueTypeColors = (mode = 'light') => ({
  technician: {
    bg: mode === 'dark' ? '#1a3a5c' : '#e3f2fd',
    border: '#2196f3',
    text: mode === 'dark' ? '#64b5f6' : '#1565c0',
  },
  janitor: {
    bg: mode === 'dark' ? '#1b3d1b' : '#e8f5e9',
    border: '#4caf50',
    text: mode === 'dark' ? '#81c784' : '#2e7d32',
  },
  it_technician: {
    bg: mode === 'dark' ? '#2d1b4a' : '#f3e5f5',
    border: '#9c27b0',
    text: mode === 'dark' ? '#ce93d8' : '#7b1fa2',
  },
});

// Legacy exports for backward compatibility
// These return light mode values by default
export const priorityColors = getPriorityColors('light');
export const statusColors = getStatusColors('light');
export const roleColors = getRoleColors('light');
export const overdueColors = getOverdueColors('light');
export const issueTypeColors = getIssueTypeColors('light');
